import { useCallback, useEffect, useMemo } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { ImageSizes } from '../../../image/ImageSizes'
import ImageLibraryUpload from '../common/ImageLibraryUpload'
import {
  clearThumbnail,
  reset,
  setBackgroundColor,
  setDominantColors,
  setFontColor,
  setProposalPhraseBottom,
  setProposalPhraseTop,
  setThumbnail,
} from './thumbnailRecommendSlice'
import ColorPicker from '../common/ColorPicker'
import PropTypes from 'prop-types'
import TextInput from '../../../../components/form/TextInput'
import Preview from '../../preview/Preview'
import { Assets, FontColorNames, TalkChannelFontColors } from '../../../Codes'
import RadioButton from '../../../../components/form/RadioButton'
import { checkLegalNotice, getImageColors } from '../../creativeUtil'
import ErrorText from '../../../../components/form/ErrorText'
import HelpTooltip from '../../../../components/tooltip/HelpTooltip'
import { GitbookLinks } from '../../../../utils/constants'
import { setBothLegalNoticeChecked } from '../basic/basicCreativeSlice'

const ThumbnailRecommend = ({ disabled, errors }) => {
  const dispatch = useDispatch()
  const {
    basicCreative: {
      licenseNumber,
      license,
      bothLegalNoticeChecked,
      licenseReview,
    },
    buildCreative: { channel },
    assets: {
      thumbnailRecommend: {
        dominantColors,
        thumbnail,
        backgroundColor,
        proposalPhraseTop,
        proposalPhraseBottom,
        fontColor,
      },
    },
  } = useSelector(state => state.creative, shallowEqual)
  const isLegalNotice = useMemo(() => checkLegalNotice(channel), [channel])

  const handleImageUpload = useCallback(
    async selectedImages => {
      const [image] = selectedImages
      dispatch(setThumbnail(image))
      const colors = await getImageColors(image.url)
      dispatch(setDominantColors(colors))
    },
    [dispatch]
  )
  const handleImageDelete = useCallback(
    () => dispatch(clearThumbnail()),
    [dispatch]
  )
  const handleBackgroundColorChange = useCallback(
    value => dispatch(setBackgroundColor(value)),
    [dispatch]
  )
  const handleProposalPhraseTopChange = useCallback(
    value => dispatch(setProposalPhraseTop(value)),
    [dispatch]
  )
  const handleProposalPhraseBottomChange = useCallback(
    value => dispatch(setProposalPhraseBottom(value)),
    [dispatch]
  )

  useEffect(() => () => dispatch(reset()), [dispatch])
  useEffect(() => {
    if (
      licenseReview &&
      license.recommendLicenseNumber.trim() !== '' &&
      license.customLicenseNumber.trim() !== ''
    ) {
      dispatch(setBothLegalNoticeChecked(true))
    }
  }, [dispatch, licenseReview, license])

  return (
    <div className="cont_material">
      <p className="desc_subject">
        <strong className="subtit_subject2">추천소재</strong>분절 키워드 매칭 시
        노출되는 소재입니다.
      </p>
      <div className="group_material">
        <div className="box_material">
          <div className="inner_material">
            <strong className="screen_out">기본설정</strong>
            <div className="material_item">
              <div className="tit_itemwrap">
                <strong className="tit_item">채널명</strong>
              </div>
              <div className="desc_item">
                {channel?.talkChannel?.name || '비즈채널을 선택하세요.'}
              </div>
            </div>
            <div className="material_item">
              <div className="tit_itemwrap">
                <strong className="tit_item">썸네일 이미지</strong>
              </div>
              <div className="desc_item">
                <ImageLibraryUpload
                  handleImageUpload={handleImageUpload}
                  onClickDelete={handleImageDelete}
                  maxCount={1}
                  requiredImageSize={[ImageSizes['348x230']]}
                  imageUrl={thumbnail?.url}
                  imageName={thumbnail?.name}
                  imageSize={thumbnail?.size}
                  disabled={disabled}
                  guideLink={GitbookLinks.TALK_CHANNEL_REGISTRATION}
                />
                <ErrorText error={errors?.thumbnail} />
              </div>
            </div>
            <div className="material_item">
              <div className="tit_itemwrap">
                <strong className="tit_item">배경 색상</strong>
              </div>
              <div className="desc_item">
                <ColorPicker
                  disabled={disabled}
                  value={backgroundColor}
                  colors={dominantColors}
                  onChange={handleBackgroundColorChange}
                  error={errors?.backgroundColor}
                />
              </div>
            </div>
            <div className="material_item">
              <div className="tit_itemwrap">
                <strong className="tit_item">홍보문구</strong>
              </div>
              <div className="desc_item">
                <div className="multi_row">
                  <div className="subtit_itemwrap">
                    <em className="subtit_item">상단 홍보문구</em>
                  </div>
                  <TextInput
                    disabled={disabled}
                    value={proposalPhraseTop}
                    onValueChange={handleProposalPhraseTopChange}
                    maxLength={13}
                    placeholder="상단 홍보문구를 입력하세요."
                    trim
                    error={errors?.proposalPhraseTop}
                  />
                  <ErrorText error={errors?.proposalPhraseTop} />
                </div>
                <div className="multi_row">
                  <div className="subtit_itemwrap">
                    <em className="subtit_item">하단 홍보문구</em>
                    {isLegalNotice && (
                      <HelpTooltip>
                        법적고지문구 입력 시 추천소재의 하단 홍보문구는 노출되지
                        않습니다.
                      </HelpTooltip>
                    )}
                  </div>
                  <TextInput
                    disabled={
                      disabled ||
                      (isLegalNotice &&
                        (licenseNumber.trim() !== '' || bothLegalNoticeChecked))
                    }
                    value={proposalPhraseBottom}
                    onValueChange={handleProposalPhraseBottomChange}
                    maxLength={13}
                    placeholder="하단 홍보문구를 입력하세요."
                    trim
                    error={
                      !bothLegalNoticeChecked &&
                      licenseNumber.trim() === '' &&
                      errors?.proposalPhraseBottom
                    }
                  />
                  <ErrorText
                    error={
                      !bothLegalNoticeChecked &&
                      licenseNumber.trim() === '' &&
                      errors?.proposalPhraseBottom
                    }
                  />
                </div>
              </div>
            </div>
            <div className="material_item">
              <div className="tit_itemwrap">
                <strong className="tit_item">
                  문구 색상
                  <HelpTooltip>
                    문구 색상은 배경 색상 지정 후 선택 가능합니다.
                  </HelpTooltip>
                </strong>
              </div>
              <div className="desc_item">
                <div className="multi_row">
                  <div className="subtit_itemwrap">
                    <em className="subtit_item">홍보문구</em>
                  </div>
                  <div className="item_filter">
                    <RadioButton.Group className="filter_radio">
                      <RadioButton
                        disabled={disabled || backgroundColor === undefined}
                        checkValue={fontColor}
                        value={TalkChannelFontColors.BLACK}
                        label={FontColorNames[TalkChannelFontColors.BLACK]}
                        onValueChange={() =>
                          dispatch(setFontColor(TalkChannelFontColors.BLACK))
                        }
                      />
                      <RadioButton
                        disabled={disabled || backgroundColor === undefined}
                        checkValue={fontColor}
                        value={TalkChannelFontColors.WHITE}
                        label={FontColorNames[TalkChannelFontColors.WHITE]}
                        onValueChange={() =>
                          dispatch(setFontColor(TalkChannelFontColors.WHITE))
                        }
                      />
                    </RadioButton.Group>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <Preview
          type={Assets.THUMBNAIL_RECOMMEND.name}
          data={{
            dominantColors,
            thumbnail,
            backgroundColor,
            proposalPhraseTop,
            proposalPhraseBottom,
            fontColor,
          }}
        />
      </div>
    </div>
  )
}
ThumbnailRecommend.propTypes = {
  disabled: PropTypes.bool,
  errors: PropTypes.shape({
    thumbnail: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    backgroundColor: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    proposalPhraseTop: PropTypes.oneOfType([PropTypes.bool, PropTypes.string]),
    proposalPhraseBottom: PropTypes.oneOfType([
      PropTypes.bool,
      PropTypes.string,
    ]),
  }),
}
export default ThumbnailRecommend
